import type { ReactNode, PropsWithChildren } from 'react';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { TopLevelSpacer } from '@atlaskit/side-nav-items/top-level-spacer';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';

import { ConfluenceWorkspaceAri, IdentityUserAri } from '@atlassian/ari';
import type {
	GlobalShortcutLinkItem,
	fnGetFilteredProductLinks,
} from '@atlassian/global-shortcuts';
import { useJira } from '@atlassian/global-shortcuts/jira';
import {
	InvitedAppShortcutsContainer,
	useInvitedAppProductLinks,
	ShortcutsMenu,
	type ShortcutItemsConfig,
} from '@atlassian/cross-join-nudges';

import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	useRouteActions,
	useRouteDataRef,
	useTransitionId,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { LoadableLazy } from '@confluence/loadable';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { fg } from '@confluence/feature-gating';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useFlyoutActions } from '@confluence/flyout-store';
import { isTeamsAppEnabled } from '@confluence/teams-app-config/entry-points/isTeamsAppEnabled.ts';
import { expVal } from '@confluence/feature-experiments';
import { SimplifyNavNewUserSpotlight } from '@confluence/experiment-simplify-nav-new-user/entry-points/SimplifyNavNewUserSpotlight.ts';
import { useSimplifiedNavSpotlight } from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifiedNavSpotlight.ts';
import { useSimplifyNavNewUserSpotlightStoreActions } from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifyNavNewUserSpotlightStore.ts';

import { CompanyHubItem } from '../PlatformNavigation/AppShortcuts/apps/CompanyHubItem/CompanyHubItem.tsx';
import type { MenuId } from '../PlatformNavigation/PlatformNavigationTypes.ts';

import { usePlatformCustomizationsWithSimplifyNav } from './usePlatformCustomizationsWithSimplifyNav.ts';
import { GLOBAL_NAVIGATION_METRIC } from './perf.config.ts';
import { useMenuItems } from './useMenuItems.ts';
import { GlobalAppShortcuts } from './GlobalAppShortcuts/GlobalAppShortcutsWrapper.tsx';
import { useResolveInvitedAppShortcuts } from '../PlatformNavigation/AppShortcuts/hooks/useResolveInvitedAppShortcuts.tsx';
import { MoreMenuItem } from './MoreMenuItem.tsx';
import { CustomHomeItem } from './GlobalItems/CustomHomeItem.tsx';
import { ForYouItem } from './GlobalItems/ForYouItem.tsx';
import { RecentItem } from './GlobalItems/RecentItem.tsx';
import { StarredItem } from './GlobalItems/StarredItem.tsx';
import { SpacesItem } from './GlobalItems/SpacesItem.tsx';
import { AppsItem } from './GlobalItems/AppsItem/index.tsx';
import { TopicsItem } from './GlobalItems/TopicsItem.tsx';
import { GoalsItem } from './GlobalItems/GoalsItem.tsx';
import { TeamsItem } from './GlobalItems/TeamsItem.tsx';
import { DraftsItem } from './GlobalItems/DraftsItem.tsx';
import { TasksItem } from './GlobalItems/TasksItem.tsx';
import { TemplatesItem } from './GlobalItems/TemplatesItem.tsx';
import { CustomizeSidebarItem } from './GlobalItems/CustomizeSidebarItem.tsx';
import type {
	GlobalNavigationComponentQuery$data,
	GlobalNavigationComponentQuery$variables,
} from './__generated__/GlobalNavigationComponentQuery.graphql.ts';
import { TeamsAppShortcutLoader } from './GlobalItems/TeamsAppShortcutItem/TeamsAppShortcutItemLoader.tsx';
import { ReorderShortcutsContainer } from './reorderShortcutsContainer.tsx';

type SettingsMenuItem = NonNullable<
	NonNullable<
		NonNullable<GlobalNavigationComponentQuery$data['settings_navigationCustomisation']>['sidebar']
	>['nodes']
>[number];

const RemovedSpotlightTarget = componentWithFG(
	'remove_unused_spotlight_targets',
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
	({ name, children }: { name: string } & PropsWithChildren<{}>) => (
		<SpotlightTarget name={name}>{children}</SpotlightTarget>
	),
);

const CustomizationModalLoader = LoadableLazy({
	loader: async () => {
		const { CustomizationModalComponent } = await import(
			/* webpackChunkName: "loadable-CustomizationModal" */ './CustomizeSidebar/CustomizationModal.tsx'
		);
		return CustomizationModalComponent;
	},
});
export const PLATFORM_MENU_ID_PREFIX = 'confluence.sidebar.';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireMoreMenuClickedAnalytics,
);

const hasAccess = (accessStatus: AccessStatus | null) =>
	![AccessStatus.ANONYMOUS_ACCESS, AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS, null].includes(
		accessStatus,
	);

const hasGlobalAppShortcutsAccess = (accessStatus: AccessStatus | null) =>
	![
		AccessStatus.ANONYMOUS_ACCESS,
		AccessStatus.EXTERNAL_SHARE_ACCESS,
		AccessStatus.EXTERNAL_COLLABORATOR_ACCESS,
		AccessStatus.NOT_PERMITTED,
		null,
	].includes(accessStatus);

type GlobalNavigationComponentProps = {
	productSideNavigation?: ReactNode;
	isOnAnySettingsScreen?: boolean;
	isCollapsed?: boolean;
};

const GlobalItemComponents = {
	customHome: CustomHomeItem,
	yourWork: ForYouItem,
	companyHub: CompanyHubItem,
	recent: RecentItem,
	starred: StarredItem,
	spaces: SpacesItem,
	apps: AppsItem,
	goals: GoalsItem,
	topics: TopicsItem,
	teams: TeamsItem,
	drafts: DraftsItem,
	tasks: TasksItem,
	templates: TemplatesItem,
	customize: CustomizeSidebarItem,
};

export const GlobalNavigationComponent = ({
	productSideNavigation,
	isOnAnySettingsScreen,
	isCollapsed,
}: GlobalNavigationComponentProps) => {
	const { accessStatus, cloudId, userId, activationId } = useSessionData();
	const [peekingId, setPeekingId] = useState<MenuId | undefined>(undefined);
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState<boolean>(false);
	const routeDataRef = useRouteDataRef();
	const { push } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);
	const { openFlyout, closeFlyout } = useFlyoutActions();
	const transitionId = useTransitionId();
	const teamsAppEnabled = isTeamsAppEnabled();
	const [getFilteredProductLinks, setGetFilteredProductLinks] = useState<
		fnGetFilteredProductLinks | undefined
	>(undefined);

	const [isCustomizeSidebarDialogVisible, setIsCustomizeSidebarDialogVisible] =
		useState<boolean>(false);

	const jiraShortcut: GlobalShortcutLinkItem = useJira('');
	const invitedAppShortcutsExperiment = useResolveInvitedAppShortcuts();
	const isEligibleToSeeInvitedAppShortcuts = useMemo(
		() =>
			// we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('twcg-460-personalised-invite-notifications-m2-gate') &&
			invitedAppShortcutsExperiment.ineligibilityReason === null &&
			invitedAppShortcutsExperiment.invitedApps?.jira?.eligible === true,
		[invitedAppShortcutsExperiment],
	);

	useEffect(() => {
		if (transitionId && transitionId > 0) {
			//on any transition close open flyout. On initial Load transitionId = 0
			closeFlyout();
		}
	}, [closeFlyout, transitionId]);

	const { setHasOpenedCustomize } = useSimplifyNavNewUserSpotlightStoreActions();

	const { data, error } = useQueryInterop<
		GlobalNavigationComponentQuery$data,
		GlobalNavigationComponentQuery$variables
	>(GlobalNavigationComponentQuery, {
		client: getAGGClient(),
		variables: {
			entityAri: ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString(),
			ownerAri: userId ? IdentityUserAri.create({ userId }).toString() : '',
		},
		skip: !userId || !activationId,
	});

	const platformCustomizations = useMemo(() => {
		if (data?.settings_navigationCustomisation?.sidebar?.nodes) {
			return data.settings_navigationCustomisation.sidebar.nodes
				.filter((node): node is NonNullable<typeof node> => node != null)
				.map((node) => ({
					menuId: node.menuId?.replace(PLATFORM_MENU_ID_PREFIX, '') as MenuId,
					visible: Boolean(node.visible),
				}));
		}
		return [];
	}, [data]);

	const { platformCustomizationsWithSimplifyNav, navStyle, isWithinD0 } =
		usePlatformCustomizationsWithSimplifyNav(platformCustomizations);

	//only show customization and fetch data if we meet these conditions
	const showCustomization = !!activationId && hasAccess(accessStatus);

	// only render global app shortcuts if we meet these conditions
	const showGlobalAppShortcuts = hasGlobalAppShortcutsAccess(accessStatus);

	const { globalMenuItems, moreMenuItems, appShortcutMenuItems, allMenuItems } = useMenuItems(
		showCustomization,
		accessStatus,
		platformCustomizationsWithSimplifyNav,
		getFilteredProductLinks,
	);

	const { shouldShowSpotlight, shouldPersistCustomizeOpen } = useSimplifiedNavSpotlight({
		globalMenuItems,
		navStyle,
		isWithinD0,
	});

	const moreMenuOnClickCallback = useCallback(
		(menuId: MenuId) => {
			setIsMoreMenuOpen(!isMoreMenuOpen);

			// TODO: it would be great if we could refactor the following menu-specific logic into the respective menu components
			if (menuId === 'customize') {
				setIsCustomizeSidebarDialogVisible(true);
				// Persist for treatment users with Recent in main nav (includes D0) — so D0 users who open customize don't see spotlight when they become D1+
				if (shouldPersistCustomizeOpen) {
					setHasOpenedCustomize();
				}
				void fireClickAnalytics('customiseSidebar');
			} else {
				void fireClickAnalytics(menuId);
			}

			if (menuId === 'recent' || menuId === 'starred' || menuId === 'spaces') {
				openFlyout(menuId);
				setPeekingId(menuId);
			}

			if (menuId === 'apps') {
				push('/wiki/marketplace/discover');
			}
		},
		[
			fireClickAnalytics,
			isMoreMenuOpen,
			openFlyout,
			push,
			setHasOpenedCustomize,
			shouldPersistCustomizeOpen,
		],
	);

	const visibleMenuIds = useMemo(() => {
		return appShortcutMenuItems.reduce<string[]>(
			(acc, item) => (item.visible ? [...acc, item.menuId] : acc),
			[],
		);
	}, [appShortcutMenuItems]);

	const isInvitedAppShortcutsEnabled = useMemo(
		() =>
			isEligibleToSeeInvitedAppShortcuts &&
			expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
		[isEligibleToSeeInvitedAppShortcuts],
	);

	const invitedAppShortcutsConfig: ShortcutItemsConfig = useMemo(() => {
		if (!isInvitedAppShortcutsEnabled) {
			return {};
		}
		return {
			jira: jiraShortcut,
		};
	}, [isInvitedAppShortcutsEnabled, jiraShortcut]);

	const { invitedAppProductLinks, handleUpdateInvitedAppProductLinks } = useInvitedAppProductLinks({
		shortcutItemsConfig: invitedAppShortcutsConfig,
	});

	useEffect(() => {
		if (getFilteredProductLinks && fg('twcg-460-personalised-invite-notifications-m2-gate')) {
			invitedAppShortcutsExperiment.updateFromAppAccessFilter({
				getFilteredProductLinks,
				limitToOnlyApsKeys: ['JIRA_SOFTWARE'],
				visibleMenuIds,
			});
		}
	}, [
		appShortcutMenuItems,
		getFilteredProductLinks,
		invitedAppShortcutsExperiment,
		visibleMenuIds,
	]);

	// The visible app shortcuts:
	// "Company Hub" is the first and only app shortcut at the time of this writing so the naming and abstraction here are as simple as possible.
	let companyHubAppShortcut: ReactNode = null;

	// The item "Customize sidebar" when it's one of the visible global items and app shortcuts:
	let globalCustomizeMenuItem: ReactNode = null;

	const moreMenuItem = (
		<MoreMenuItem
			moreMenuItems={moreMenuItems}
			isOpen={isMoreMenuOpen}
			onClick={moreMenuOnClickCallback}
			toggleMenu={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
			peekingId={peekingId}
			isCollapsed={isCollapsed}
		/>
	);

	useEffect(() => {
		if (isEligibleToSeeInvitedAppShortcuts) {
			invitedAppShortcutsExperiment.fireFeatureExposed({
				isEnabled: expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
			});
		}
	}, [isEligibleToSeeInvitedAppShortcuts, invitedAppShortcutsExperiment]);

	return (
		<RemovedSpotlightTarget name="nav4-spotlight-0">
			<MenuList>
				{!isOnAnySettingsScreen && (
					<SpotlightTarget name="enhance-orientation-spotlight-tour-target-3">
						<div data-vc="nav4-globalItems">
							{globalMenuItems.map(({ menuId }) => {
								const Component = GlobalItemComponents[menuId];
								let node: ReactNode = null;
								if (Component) {
									const baseNode = (
										<Component
											key={menuId}
											setIsCustomizeSidebarDialogVisible={setIsCustomizeSidebarDialogVisible}
										/>
									);
									// Spotlight anchored to Recent when in main nav (ACTIVATION-2059).
									// Always render the wrapper and pass `shouldShowSpotlight`; when not eligible,
									// the wrapper returns only the Recent item with no spotlight UI.
									node =
										menuId === 'recent' ? (
											<SimplifyNavNewUserSpotlight
												key={menuId}
												shouldShowSpotlight={shouldShowSpotlight}
											>
												{baseNode}
											</SimplifyNavNewUserSpotlight>
										) : (
											baseNode
										);
									// All global items stay together and all app shortcuts stay together, they are separate groups.
									// I.e. the app shortcuts should be after a peeking global item (if any).
									// This is the render of the global items section so skip the app shortcuts (of which there is only Company Hub at Team '25 US) here and render them later.
									if (menuId === 'companyHub') {
										companyHubAppShortcut = node;
										return null;
									} else if (menuId === 'customize') {
										// The item "Customize sidebar" should be:
										// - after the app shortcuts if any;
										// - visually divided from the preceeding global items or app shortcuts.
										globalCustomizeMenuItem = node;
										return null;
									}
								}
								return node;
							})}
							{
								// we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
								// eslint-disable-next-line confluence-feature-gating/no-preconditioning
								isInvitedAppShortcutsEnabled &&
									showGlobalAppShortcuts &&
									fg('nav4_global_links_confluence') && (
										<InvitedAppShortcutsContainer
											shortcutsConfig={invitedAppShortcutsConfig}
											resolverResult={invitedAppShortcutsExperiment}
										>
											{({ referrableShortcuts }) => (
												<ReorderShortcutsContainer
													shortcutsConfig={referrableShortcuts}
													customizationMenuConfig={platformCustomizationsWithSimplifyNav}
												>
													{({ shortcuts }) => (
														<ShortcutsMenu
															shortcutItems={shortcuts}
															product="confluence"
															invitedAppProductLinks={invitedAppProductLinks}
														/>
													)}
												</ReorderShortcutsContainer>
											)}
										</InvitedAppShortcutsContainer>
									)
							}
							{moreMenuItems.map((result) => {
								const Component = GlobalItemComponents[result.menuId];
								return (
									Component && (
										<Component
											key={result.menuId}
											isHidden
											peekingId={peekingId}
											setPeekingId={setPeekingId}
											setIsCustomizeSidebarDialogVisible={setIsCustomizeSidebarDialogVisible}
										/>
									)
								);
							})}
							{/* The app shortcuts section is after and visually divided from the global items section:  */}
							{/* DEPRECATED: App Shortcuts is now being managed by Global App Shortcuts above  */}
							{companyHubAppShortcut && !fg('nav4_global_links_confluence') && (
								<>
									<TopLevelSpacer />
									{companyHubAppShortcut}
								</>
							)}
							{teamsAppEnabled && !fg('nav4_global_links_confluence') && (
								<>
									{/* Only show the divider if Company Hub is not also present */}
									{!companyHubAppShortcut && <TopLevelSpacer />}
									<TeamsAppShortcutLoader />
								</>
							)}
							{/* The item "Customize sidebar" is after and visually divided from the global items and/or app shortcuts sections: */}
							{globalCustomizeMenuItem && (
								<>
									<TopLevelSpacer />
									{globalCustomizeMenuItem}
								</>
							)}
							{showCustomization && !fg('nav4_global_links_confluence') && (
								<>
									{/* "More" is visually divided from the preceding global items and/or app shortcuts:*/}
									{(companyHubAppShortcut || teamsAppEnabled) && <TopLevelSpacer />}
									{moreMenuItem}
									<TopLevelSpacer />
								</>
							)}
							{error && <ErrorDisplay error={error} />}
							{isCustomizeSidebarDialogVisible && (
								<CustomizationModalLoader
									onClose={() => setIsCustomizeSidebarDialogVisible(false)}
									productItemsData={allMenuItems}
									appItemsData={fg('nav4_global_links_confluence') ? appShortcutMenuItems : []}
									navigationCustomisationId={data?.settings_navigationCustomisation?.id || ''}
								/>
							)}
							<PageSegmentLoadEnd metric={GLOBAL_NAVIGATION_METRIC} />
						</div>
					</SpotlightTarget>
				)}
				{fg('nav4_global_links_confluence') && <TopLevelSpacer />}
			</MenuList>

			{productSideNavigation}

			{/* Global App Shortcuts: Platform Component for supporting Global App Shortcuts */}
			{!isOnAnySettingsScreen && showGlobalAppShortcuts && fg('nav4_global_links_confluence') && (
				<MenuList>
					<TopLevelSpacer />
					<GlobalAppShortcuts
						orderedMenuConfig={platformCustomizationsWithSimplifyNav}
						onUpdateGetFilteredProductLinks={(fn: fnGetFilteredProductLinks) => {
							setGetFilteredProductLinks(() => fn);

							handleUpdateInvitedAppProductLinks(fn);
						}}
						isInvitedAppShortcutsEnabled={isInvitedAppShortcutsEnabled}
					/>
					{moreMenuItem}
				</MenuList>
			)}
		</RemovedSpotlightTarget>
	);
};

export const updateNavigationCustomization = (
	variables: GlobalNavigationComponentQuery$variables,
	nodes: SettingsMenuItem[],
	id: string,
) => {
	writeQueryInterop({
		query: GlobalNavigationComponentQuery,
		variables,
		data: {
			settings_navigationCustomisation: {
				__typename: 'SettingsNavigationCustomisation',
				id,
				sidebar: {
					__typename: 'SettingsMenuItemConnection',
					nodes,
				},
			},
		},
	});
};

const GlobalNavigationComponentQuery = graphql`
	query GlobalNavigationComponentQuery($entityAri: ID!, $ownerAri: ID!) {
		settings_navigationCustomisation(entityAri: $entityAri, ownerAri: $ownerAri) {
			id
			sidebar {
				nodes {
					menuId
					visible
				}
			}
		}
	}
`;
